import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ROUTES } from "@/navigation/routes";

// hooks 
import { useAuthStore } from "@/state/useAuthStore";

// components 
import Loader from "./components/utils/Loader";
import ProtectedRoute from "./routes/ProtectedRoute";
import { ToastWrapper } from "./utils/toast";
import AdminLayout from "./layouts/AdminLayout";
import { EnableSoundBanner } from "./components/ui/EnableSoundBanner";

// config /hookks
import { requestForToken } from "./config/firebase";
import { useNotificationListener } from "@/features/notification/hooks/listenNotification";

// pages 
const LoginPage = lazy(() => import("./pages/Login"));
const Logout = lazy(() => import("./pages/logout"));
const Dashboard = lazy(() => import("./pages/DashBoard"));
const Settings = lazy(() => import("./pages/Settings"));
const UsersList = lazy(() => import("./pages/UsersList"));
const NotFound = lazy(() => import("./pages/404"));
const Orders = lazy(() => import("./pages/OrdersList"));
const NewPartnersList = lazy(() => import("@/features/NewPartner/NewPartnersList"));
const PartnersList = lazy(() => import("./features/partner/PartnersList"));
const CategoriesList = lazy(() => import("./pages/Categories"));
const SupportsList = lazy(() => import("./pages/SupportsList"));
const SupportChat = lazy(() => import("./pages/SupportChat"));

const ProductsList = lazy(() => import("@/features/product/ProductsList"));

const CouponsList = lazy(() => import("./pages/CouponsList"));

const OrdersReviewList = lazy(() => import("./pages/OrdersReviewList"));
const PartnersReviewList = lazy(() => import("./pages/PartnersReviewList"));
const ProductsReviewList = lazy(() => import("./pages/ProductsReviewList"));

const CurrenciesExchangeList = lazy(() => import("./pages/CurrenciesExchangeList"));
const BillsList = lazy(() => import("./pages/bills"));
const AdminsList = lazy(() => import("./pages/AdminsList"));

const EarningReport = lazy(() => import("./features/earningReport/EarningReport"));
const TopEntitiesReport = lazy(() => import("@/features/topEntities/topEntities"));



function App() {
  const { isAuthenticated, token } = useAuthStore();
  const [loading, setLoading] = useState(true);

  useNotificationListener();

  useEffect(() => {
    setLoading(false);
  }, []);

  // services
  useEffect(() => {
    if (!token) return;
    requestForToken();
  }, [token]);

  if (loading) return <Loader />;

  return (
    <>
      <Router>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Protected routes */}
            <Route element={<ProtectedRoute isAuthenticated={isAuthenticated()} />}>
              <Route element={<AdminLayout />}>
                <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                <Route path={ROUTES.SETTINGS} element={<Settings />} />
                <Route path={ROUTES.USERS} element={<UsersList />} />
                <Route path={ROUTES.ORDERS} element={<Orders />} />
                <Route path={ROUTES.NEW_PARTNERS} element={<NewPartnersList />} />
                <Route path={ROUTES.PARTNERS} element={<PartnersList />} />
                <Route path={ROUTES.PRODUCTS} element={<ProductsList />} />
                <Route path={ROUTES.CATEGORIES} element={<CategoriesList />} />
                <Route path={ROUTES.SUPPORTS} element={<SupportsList />} />
                <Route path={ROUTES.SUPPORT_CHAT} element={<SupportChat />} />
                <Route path={ROUTES.COUPONS} element={<CouponsList />} />
                <Route path={ROUTES.REVIEWS_ORDERS} element={<OrdersReviewList />} />
                <Route path={ROUTES.REVIEWS_PRODUCTS} element={<ProductsReviewList />} />
                <Route path={ROUTES.REVIEWS_PARTNERS} element={<PartnersReviewList />} />
                <Route path={ROUTES.BILLS} element={<BillsList />} />
                <Route path={ROUTES.CURRENCIES_EXCHANGE} element={<CurrenciesExchangeList />} />
                <Route path={ROUTES.ADMINS} element={<AdminsList />} />
                <Route path={ROUTES.EARNING_REPORT} element={<EarningReport />} />
                <Route path={ROUTES.TOP_ENTITIES_REPORT} element={<TopEntitiesReport />} />
              </Route>
            </Route>

            {/* Public routes */}
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.LOGOUT} element={<Logout />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>

      {/* popups / mini modals */}
      <ToastWrapper />
      <EnableSoundBanner />

    </>
  );
}

export default App;